/* eslint-disable react-hooks/rules-of-hooks */
import { useState, useEffect } from 'react'

import { generateArray } from './generateArray'

export const ButtonTypes = {
  PAGE_BUTTON: 'PAGE_BUTTON',
  DOT_BUTTON: 'DOT_BUTTON',
} as const

export type ButtonType = typeof ButtonTypes[keyof typeof ButtonTypes]

export interface UsePaginationProps {
  /**
   * The total number of items to paginate.
   * @required
   */
  totalRecords: number
  /**
   * The number of items to display on each page.
   * @required
   */
  itemsPerPage: number
  /**
   * The current page number.
   * @default 0
   */
  page?: number
  /**
   * function to be called when the page number changes.
   */
  onChange?: { (currentIndex: number): void }
}

export type AccessibilityProps = {
  onClick?: { (event: React.MouseEvent): void }
  'aria-current'?: boolean
  value?: number
  tabIndex?: number
  'aria-disabled'?: boolean
}

export type PaginationButtonProps = {
  type: ButtonType
  label?: string | number
  key?: number | string
  props?: AccessibilityProps
  disabled?: boolean
}

export type UsePagination = {
  buttonList: PaginationButtonProps[]
}

const TOTAL_PAGE_NUMBERS_TO_VIEW = 5

/**
 * @hook
 * @name usePagination
 * @description usePagination hook
 * @tutorial https://flora.gb.tech/docs/components/core/pagination
 */
export const usePagination = ({
  totalRecords,
  itemsPerPage,
  onChange,
  page,
}: UsePaginationProps): UsePagination => {
  const [currentPage, setCurrentPage] = useState(0)

  const totalPageCount = Math.ceil(totalRecords / Number(itemsPerPage ? itemsPerPage : 1))

  useEffect(() => {
    setCurrentPage(Math.max(page as number, 0))
  }, [page])

  const getAccessibilityProps = (item: number): AccessibilityProps =>
    typeof item === 'number'
      ? {
          'aria-current': item === currentPage,
          value: item + 1,
          tabIndex: 0,
          'aria-disabled': false,
          onClick: (event: React.MouseEvent) => {
            event.preventDefault()
            setCurrentPage(item)

            /* istanbul ignore else */
            if (typeof onChange === 'function') {
              onChange(item)
            }
          },
        }
      : {
          tabIndex: 0,
        }

  const getList = (): (number | string)[] => {
    const list: (number | string)[] = generateArray(totalPageCount)
    const lastPage = list.length - 1

    if (totalPageCount <= TOTAL_PAGE_NUMBERS_TO_VIEW + 1) {
      return list
    }

    if (currentPage < TOTAL_PAGE_NUMBERS_TO_VIEW - 1) {
      return [...list.slice(0, TOTAL_PAGE_NUMBERS_TO_VIEW), 'right', list[lastPage]]
    }

    if (
      currentPage >= TOTAL_PAGE_NUMBERS_TO_VIEW - 1 &&
      currentPage <= totalPageCount - TOTAL_PAGE_NUMBERS_TO_VIEW
    ) {
      const newList = list.slice(currentPage - 1, currentPage + 3).concat(['right', list[lastPage]])

      return [0, 'left', ...newList]
    }

    const newList = list.slice(totalPageCount - TOTAL_PAGE_NUMBERS_TO_VIEW, totalPageCount)

    return [0, 'left', ...newList]
  }

  const mapPageNumbersToButton = (item: number | string): PaginationButtonProps =>
    typeof item === 'number'
      ? {
          type: ButtonTypes.PAGE_BUTTON,
          label: item + 1,
          key: item + 1,
          props: getAccessibilityProps(item),
        }
      : {
          type: ButtonTypes.DOT_BUTTON,
          key: item,
          label: '...',
        }

  const buttonList = getList().map(mapPageNumbersToButton)

  return {
    buttonList,
  }
}
